import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API_URL = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_URL,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default api;

export const auth = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  me: () => api.get('/auth/me'),
};

export const dashboard = {
  getStats: () => api.get('/dashboard/stats'),
};

export const categories = {
  list: () => api.get('/categories'),
  create: (data) => api.post('/categories', data),
  update: (id, data) => api.put(`/categories/${id}`, data),
  delete: (id) => api.delete(`/categories/${id}`),
};

export const products = {
  list: (categoryId) => api.get('/products', { params: { category_id: categoryId } }),
  create: (data) => api.post('/products', data),
  update: (id, data) => api.put(`/products/${id}`, data),
  delete: (id) => api.delete(`/products/${id}`),
  toggleActive: (id) => api.patch(`/products/${id}/toggle-active`),
  syncStatus: () => api.get('/products/sync-status'),
};

export const uploads = {
  image: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload/image', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

export const customers = {
  list: (params) => api.get('/customers', { params }),
  get: (id) => api.get(`/customers/${id}`),
  create: (data) => api.post('/customers', data),
  update: (id, data) => api.put(`/customers/${id}`, data),
  delete: (id) => api.delete(`/customers/${id}`),
  addresses: (customerId) => api.get(`/customers/${customerId}/addresses`),
  createAddress: (customerId, data) => api.post(`/customers/${customerId}/addresses`, data),
  updateAddress: (customerId, addressId, data) => api.put(`/customers/${customerId}/addresses/${addressId}`, data),
  orders: (customerId) => api.get(`/customers/${customerId}/orders`),
};

export const orders = {
  list: (params) => api.get('/orders', { params }),
  create: (data) => api.post('/orders', data),
  get: (id) => api.get(`/orders/${id}`),
  updateStatus: (id, status, payload = null) => api.patch(`/orders/${id}`, payload || { status }),
  dispatch: (id) => api.post(`/orders/${id}/dispatch`),
};

export const delivery = {
  list: () => api.get('/delivery/orders'),
  create: (data) => api.post('/delivery/orders', data),
  assignDriver: (deliveryId, driverId) => api.patch(`/delivery/orders/${deliveryId}/assign`, null, { params: { driver_id: driverId } }),
  updateDeliveryType: (deliveryId, deliveryType) => api.patch(`/delivery/orders/${deliveryId}/delivery-type`, null, { params: { delivery_type: deliveryType } }),
};

export const drivers = {
  list: (params = {}) => api.get('/drivers', { params }),
  get: (id) => api.get(`/drivers/${id}`),
  create: (data) => api.post('/drivers', data),
  update: (id, data) => api.put(`/drivers/${id}`, data),
  delete: (id) => api.delete(`/drivers/${id}`),
  toggleAvailability: (id) => api.patch(`/drivers/${id}/toggle-availability`),
  getLocations: () => api.get('/drivers/locations'),
  getActiveDeliveries: () => api.get('/drivers/active-deliveries'),
};

export const paymentMethods = {
  list: () => api.get('/payment-methods'),
  create: (data) => api.post('/payment-methods', data),
  initDefaults: () => api.post('/payment-methods/init-defaults'),
};

export const tables = {
  list: () => api.get('/tables'),
  create: (data) => api.post('/tables', data),
};

// PDV (Ponto de Venda) API - Salão e Venda Rápida
export const pdv = {
  // Mesas
  tables: {
    list: (params) => api.get('/pdv/tables', { params }),
    open: (tableId, data) => api.post(`/pdv/tables/${tableId}/open`, data),
    transfer: (data) => api.post('/pdv/tables/transfer', data),
    requestBill: (tableId) => api.post(`/pdv/tables/${tableId}/request-bill`),
    free: (tableId) => api.post(`/pdv/tables/${tableId}/free`),
    // Novos endpoints para fluxo de conta
    resume: (tableId) => api.post(`/pdv/tables/${tableId}/resume`),
    closeBill: (tableId) => api.post(`/pdv/tables/${tableId}/close-bill`),
    reopenBill: (tableId) => api.post(`/pdv/tables/${tableId}/reopen-bill`),
    fixState: (tableId) => api.post(`/pdv/tables/${tableId}/fix-state`),
    resetAll: () => api.post('/pdv/tables/reset-all'),
  },
  // Comandas
  comandas: {
    list: (params) => api.get('/pdv/comandas', { params }),
    open: (params) => api.post('/pdv/comandas/open', null, { params }),
    merge: (data) => api.post('/pdv/comandas/merge', data),
  },
  // Itens do Pedido
  orders: {
    addItem: (orderId, data) => api.post(`/pdv/orders/${orderId}/items`, data),
    removeItem: (orderId, itemId, reason) => api.delete(`/pdv/orders/${orderId}/items/${itemId}`, { params: { reason } }),
    sendToKitchen: (orderId) => api.post(`/pdv/orders/${orderId}/send-to-kitchen`),
    markItemReady: (orderId, itemId) => api.post(`/pdv/orders/${orderId}/items/${itemId}/ready`),
    updateItemQty: (orderId, itemId, quantity) => api.patch(`/pdv/orders/${orderId}/items/${itemId}`, { quantity }),
    applyCoupon: (orderId, data) => api.post(`/pdv/orders/${orderId}/coupon`, data),
    removeCoupon: (orderId) => api.delete(`/pdv/orders/${orderId}/coupon`),
    applyDiscount: (orderId, data) => api.post(`/pdv/orders/${orderId}/discount`, data),
    removeDiscount: (orderId) => api.delete(`/pdv/orders/${orderId}/discount`),
    toggleServiceFee: (orderId) => api.post(`/pdv/orders/${orderId}/toggle-service-fee`),
    cancel: (orderId, data) => api.post(`/pdv/orders/${orderId}/cancel`, data),
    payment: (orderId, data) => api.post(`/pdv/orders/${orderId}/payment`, data),
    splitBill: (orderId, data) => api.post(`/pdv/orders/${orderId}/split-bill`, data),
  },
  // Delivery
  deliveryZones: {
    list: () => api.get('/pdv/delivery-zones'),
    create: (data) => api.post('/pdv/delivery-zones', data),
    calculateFee: (data) => api.post('/pdv/calculate-delivery-fee', data),
  },
  // Cupons
  coupons: {
    list: (activeOnly = true) => api.get('/pdv/coupons', { params: { active_only: activeOnly } }),
    create: (data) => api.post('/pdv/coupons', data),
  },
  // Venda Rápida
  quickSale: (data) => api.post('/pdv/quick-sale', data),
};

export const cash = {
  openSession: (data) => api.post('/cash/session/open', data),
  getCurrentSession: () => api.get('/cash/session/current'),
  closeSession: (amount) => api.post('/cash/session/close', null, { params: { closing_amount: amount } }),
};

export const kds = {
  list: (params) => api.get('/kds', { params }),
};

export const finance = {
  // Accounts
  accounts: {
    list: () => api.get('/finance/accounts'),
    create: (data) => api.post('/finance/accounts', data),
    update: (id, data) => api.put(`/finance/accounts/${id}`, data),
    delete: (id) => api.delete(`/finance/accounts/${id}`),
  },
  // Payables
  payables: {
    list: (params) => api.get('/finance/payables', { params }),
    create: (data) => api.post('/finance/payables', data),
    update: (id, data) => api.put(`/finance/payables/${id}`, data),
    pay: (id, data) => api.post(`/finance/payables/${id}/pay`, data),
    cancel: (id) => api.post(`/finance/payables/${id}/cancel`),
  },
  // Receivables
  receivables: {
    list: (params) => api.get('/finance/receivables', { params }),
    create: (data) => api.post('/finance/receivables', data),
    update: (id, data) => api.put(`/finance/receivables/${id}`, data),
    receive: (id, data) => api.post(`/finance/receivables/${id}/receive`, data),
    cancel: (id) => api.post(`/finance/receivables/${id}/cancel`),
  },
  // Transfers
  transfer: (data) => api.post('/finance/transfers', data),
  // Transactions
  transactions: {
    list: (params) => api.get('/finance/transactions', { params }),
  },
  // Cash movements
  cashMovements: {
    list: () => api.get('/finance/cash/movements'),
    create: (data) => api.post('/finance/cash/movements', data),
  },
  // Summary & Reports
  summary: () => api.get('/finance/summary'),
  dre: (days) => api.get('/finance/dre', { params: { days } }),
  statement: (params) => api.get('/finance/statement', { params }),
  // Ledger (new)
  ledger: {
    extrato: (params) => api.get('/finance/ledger/extrato', { params }),
    dre: (params) => api.get('/finance/ledger/dre', { params }),
    drilldown: (params) => api.get('/finance/ledger/dre/drilldown', { params }),
    postPayment: (data) => api.post('/finance/ledger/post-payment', null, { params: data }),
    postOrder: (orderId) => api.post(`/finance/ledger/post-order/${orderId}`),
    syncOrders: (days = 30) => api.post('/finance/ledger/sync-orders', null, { params: { days } }),
    syncCardFees: (days = 90) => api.post('/finance/ledger/sync-card-fees', null, { params: { days } }),
    syncAllFees: (days = 90) => api.post('/finance/ledger/sync-all-fees', null, { params: { days } }),
    fixAccounts: () => api.post('/finance/ledger/fix-accounts'),
    clear: () => api.delete('/finance/ledger/clear', { params: { confirm: 'CONFIRMAR_LIMPEZA' } }),
  },
  seedAccounts: () => api.post('/finance/seed-accounts'),
  reconciliation: {
    daily: (date) => api.get('/finance/reconciliation/daily', { params: { date } }),
  },
};

export const fiscal = {
  config: {
    get: () => api.get('/fiscal/config'),
    update: (data) => api.put('/fiscal/config', data),
  },
  taxProfiles: {
    list: () => api.get('/fiscal/tax-profiles'),
    create: (data) => api.post('/fiscal/tax-profiles', data),
    delete: (id) => api.delete(`/fiscal/tax-profiles/${id}`),
  },
  documents: {
    list: (params) => api.get('/fiscal/documents', { params }),
    get: (id) => api.get(`/fiscal/documents/${id}`),
    create: (data) => api.post('/fiscal/documents', data),
    cancel: (id) => api.post(`/fiscal/documents/${id}/cancel`),
    reprocess: (id) => api.post(`/fiscal/documents/${id}/reprocess`),
  },
  emitNfce: (orderId) => api.post(`/fiscal/emit-nfce/${orderId}`),
  generateXml: (orderId) => api.post(`/fiscal/generate-xml/${orderId}`),
  regenerateXml: (docId) => api.post(`/fiscal/regenerate-xml/${docId}`),
  transmitNfce: (docId) => api.post(`/fiscal/transmit-nfce/${docId}`),
  destination: {
    get: (orderId) => api.get(`/fiscal/destination/${orderId}`),
    upsert: (orderId, payload) => api.put(`/fiscal/destination/${orderId}`, payload),
    remove: (orderId) => api.delete(`/fiscal/destination/${orderId}`),
  },
  uploadCertificate: (file, password) => {
    const fd = new FormData();
    fd.append('pfx_file', file);
    fd.append('password', password);
    return api.post('/fiscal/upload-certificate', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  certificateInfo: () => api.get('/fiscal/certificate-info'),
  downloadXml: (docId, format = 'auto') => api.get(`/fiscal/documents/${docId}/xml`, { params: { format }, responseType: 'blob' }),
  validateDocument: (docId) => api.post(`/fiscal/validate/${docId}`),
  health: () => api.get('/fiscal/health'),
  previewHtml: (docId) => api.get(`/fiscal/documents/${docId}/preview`, { responseType: 'text' }),
  previewJson: (docId) => api.get(`/fiscal/documents/${docId}/preview`, { params: { format: 'json' } }),
  importXml: (data) => api.post('/fiscal/import-xml', data),
  reports: {
    summary: (days) => api.get('/fiscal/reports/summary', { params: { days } }),
  },
  stats: () => api.get('/fiscal/stats'),
  archiveSimulated: () => api.post('/fiscal/archive-simulated'),
};

export const stock = {
  // Locations
  locations: {
    list: () => api.get('/stock/locations'),
    create: (data) => api.post('/stock/locations', data),
  },
  // Items
  items: {
    list: () => api.get('/stock/items'),
    create: (data) => api.post('/stock/items', data),
    update: (id, data) => api.put(`/stock/items/${id}`, data),
    delete: (id) => api.delete(`/stock/items/${id}`),
  },
  // Conversions
  conversions: {
    create: (itemId, data) => api.post(`/stock/items/${itemId}/conversions`, data),
  },
  // Movements
  movements: {
    list: (params) => api.get('/stock/movements', { params }),
    create: (data) => api.post('/stock/movements', data),
  },
  // Balances
  balances: {
    list: (params) => api.get('/stock/balances', { params }),
  },
  // Adjustments & Transfers
  adjustments: {
    create: (data) => api.post('/stock/adjustments', data),
  },
  transfers: {
    create: (data) => api.post('/stock/transfers', data),
  },
  // Inventory Counting
  inventory: {
    items: () => api.get('/stock/inventory/items'),
    count: (data) => api.post('/stock/inventory/count', data),
    history: (params) => api.get('/stock/inventory/history', { params }),
    summary: (days = 30) => api.get('/stock/inventory/summary', { params: { days } }),
  },
  // Recipes
  recipes: {
    list: () => api.get('/stock/recipes'),
    create: (data) => api.post('/stock/recipes', data),
    update: (id, data) => api.put(`/stock/recipes/${id}`, data),
    delete: (id) => api.delete(`/stock/recipes/${id}`),
  },
  // Production Orders
  production: {
    list: () => api.get('/stock/production-orders'),
    create: (data) => api.post('/stock/production-orders', data),
  },
  // Manual Stock Entry (sem NF)
  manualEntry: {
    list: () => api.get('/stock/manual/entries'),
    create: (data) => api.post('/stock/manual/entry', data),
  },
  // Fiscal Documents
  fiscal: {
    list: () => api.get('/stock/fiscal-documents'),
    create: (data) => api.post('/stock/fiscal-documents', data),
  },
  // Financial
  receivables: {
    list: () => api.get('/stock/receivables'),
    create: (data) => api.post('/stock/receivables', data),
  },
  payables: {
    list: () => api.get('/stock/payables'),
    create: (data) => api.post('/stock/payables', data),
  },
  pay: (docType, docId) => api.post(`/stock/financial/pay/${docType}/${docId}`),
  // Deduct sale
  deductSale: (orderId) => api.post(`/stock/deduct-sale/${orderId}`),
  // Reports
  reports: {
    cmvByProduct: (days) => api.get('/stock/reports/cmv-by-product', { params: { days } }),
    abcCurve: (days) => api.get('/stock/reports/abc-curve', { params: { days } }),
    stockStatus: () => api.get('/stock/reports/stock-status'),
    marginByPeriod: (days) => api.get('/stock/reports/margin-by-period', { params: { days } }),
    stockTurnover: (days) => api.get('/stock/reports/stock-turnover', { params: { days } }),
  },
  // Product Options / Variations
  getProductOptionGroups: (productId) => api.get(`/stock/products/${productId}/option-groups`),
  createOptionGroup: (productId, data) => api.post(`/stock/products/${productId}/option-groups`, data),
  deleteOptionGroup: (groupId) => api.delete(`/stock/option-groups/${groupId}`),
  createOption: (groupId, data) => api.post(`/stock/option-groups/${groupId}/options`, data),
  updateOption: (optionId, data) => api.put(`/stock/options/${optionId}`, data),
  deleteOption: (optionId) => api.delete(`/stock/options/${optionId}`),
  getItems: () => api.get('/stock/items'),
};


// CRM API
export const crmApi = {
  dashboard: (days = 30) => api.get('/crm/dashboard', { params: { days } }),
  customers: (params = {}) => api.get('/crm/customers', { params }),
  customerProfile: (id) => api.get(`/crm/customers/${id}/profile`),
  updateTags: (id, tags) => api.patch(`/crm/customers/${id}/tags`, tags, { params: { tags } }),
  updateLgpd: (id, consent) => api.patch(`/crm/customers/${id}/lgpd`, null, { params: { consent } }),
  segments: () => api.get('/crm/segments'),
  createSegment: (data) => api.post('/crm/segments', data),
  segmentCustomers: (id) => api.get(`/crm/segments/${id}/customers`),
  deleteSegment: (id) => api.delete(`/crm/segments/${id}`),
  messages: (limit = 50) => api.get('/crm/messages', { params: { limit } }),
  sendMessage: (data) => api.post('/crm/messages/send', data),
  templates: () => api.get('/crm/templates'),
  createTemplate: (data) => api.post('/crm/templates', data),
  cancellations: (days = 30) => api.get('/crm/cancellations', { params: { days } }),
  cancellationSummary: (days = 30) => api.get('/crm/cancellations/summary', { params: { days } }),
};

// COD (Cash on Delivery) API
export const cod = {
  // Delivery COD
  deliveries: {
    getStatus: (deliveryId) => api.get(`/cod/deliveries/${deliveryId}/status`),
    registerCollection: (deliveryId, data) => api.patch(`/cod/deliveries/${deliveryId}/cod`, data),
  },
  // Driver Settlements
  settlements: {
    list: (params) => api.get('/cod/driver-settlements', { params }),
    create: (driverId, periodStart, periodEnd) => api.post('/cod/driver-settlements/create', null, { 
      params: { driver_id: driverId, period_start: periodStart, period_end: periodEnd } 
    }),
    close: (settlementId, notes) => api.post(`/cod/driver-settlements/${settlementId}/close`, { notes }),
    confirmRepass: (settlementId, data) => api.post(`/cod/driver-settlements/${settlementId}/confirm-repass`, data),
  },
  // Finance AR/AP/Ledger
  finance: {
    ar: {
      list: (params) => api.get('/cod/finance/ar', { params }),
      receive: (id, data) => api.post(`/cod/finance/ar/${id}/receive`, data),
    },
    ap: {
      list: (params) => api.get('/cod/finance/ap', { params }),
      pay: (id, data) => api.post(`/cod/finance/ap/${id}/pay`, data),
    },
    ledger: (params) => api.get('/cod/finance/ledger', { params }),
  },
  // Fiscal
  fiscal: {
    issue: (orderId, docType) => api.post('/cod/fiscal/issue', null, { params: { order_id: orderId, doc_type: docType } }),
    cancel: (documentId, reason) => api.post(`/cod/fiscal/documents/${documentId}/cancel`, null, { params: { reason } }),
    cce: (documentId, correctionText) => api.post(`/cod/fiscal/documents/${documentId}/cce`, null, { params: { correction_text: correctionText } }),
  },
};

// Combos API - Gestão de Combos
export const combos = {
  list: () => api.get('/combos'),
  get: (id) => api.get(`/combos/${id}`),
  create: (data) => api.post('/combos', data),
  update: (id, data) => api.put(`/combos/${id}`, data),
  delete: (id) => api.delete(`/combos/${id}`),
  availableProducts: () => api.get('/combos/available-products'),
  addItem: (comboId, data) => api.post(`/combos/${comboId}/items`, data),
  removeItem: (comboId, itemId) => api.delete(`/combos/${comboId}/items/${itemId}`),
};
// v2
